import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, FormControl, FormLabel, Option, Select, Stack, Typography } from '@mui/joy';
import FitterJobsPage from './FitterJobsPage';
import { listAssignableCrew } from './project-workspace/jobCard.service';
import type { AssignableProfile } from './project-workspace/jobCard.types';

const ROLE_LABEL: Record<string, string> = { fitter: 'Fitter', labourer: 'Labourer' };

/**
 * Office only: the fitter dashboard as a chosen crew member sees it, in a phone-sized frame.
 * Read-only preview; opening a job takes the office to that workspace's job cards.
 */
const FitterPreviewPage: React.FC = () => {
  const [crew, setCrew] = useState<AssignableProfile[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listAssignableCrew()
      .then((rows) => {
        if (cancelled) return;
        const siteCrew = rows.filter((row) => row.role === 'fitter' || row.role === 'labourer');
        setCrew(siteCrew);
        setSelectedId((current) => current || siteCrew[0]?.id || null);
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load crew'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const selected = crew.find((row) => row.id === selectedId) || null;

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 }, width: '100%', maxWidth: 1200, mx: 'auto', boxSizing: 'border-box' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ md: 'flex-start' }}>
        <Box sx={{ flex: '0 0 300px' }}>
          <Typography level="h3">Fitter&apos;s view</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
            See the dashboard exactly as a crew member sees it on their phone: the jobs assigned to them, grouped by day.
          </Typography>
          {loading && <CircularProgress size="sm" />}
          {error && <Alert color="danger">{error}</Alert>}
          {!loading && !error && (
            crew.length === 0 ? (
              <Typography level="body-sm">No fitters or labourers have accounts yet.</Typography>
            ) : (
              <FormControl>
                <FormLabel>View as</FormLabel>
                <Select value={selectedId} onChange={(_, value) => setSelectedId(value)}>
                  {crew.map((person) => (
                    <Option key={person.id} value={person.id}>
                      {person.full_name || person.email || 'Crew'} ({ROLE_LABEL[String(person.role)] || person.role})
                    </Option>
                  ))}
                </Select>
              </FormControl>
            )
          )}
          <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 2 }}>
            Tapping a job here opens that workspace&apos;s job cards in the office view, so nothing is changed on their behalf.
          </Typography>
        </Box>

        {selected && (
          <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                border: '10px solid',
                borderColor: 'neutral.800',
                borderRadius: '32px',
                overflow: 'hidden',
                bgcolor: 'var(--jarvis-bg-body)',
                boxShadow: 'lg',
              }}
            >
              <Stack direction="row" justifyContent="center" sx={{ py: 0.75, bgcolor: 'neutral.800' }}>
                <Chip size="sm" variant="solid" color="warning">Preview: {selected.full_name || selected.email}</Chip>
              </Stack>
              <Box sx={{ height: 720, overflowY: 'auto', px: 1 }}>
                <FitterJobsPage
                  key={selected.id}
                  viewAs={{ id: selected.id, full_name: selected.full_name, email: selected.email }}
                  jobHref={(job) => `/projects/${job.project_id}/workspace/implementation`}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default FitterPreviewPage;
